/**
 * The Tick and the Log (DESIGN §6.10a).
 *
 * TICK_OPEN reveals; TICK_CLOSE resolves; LOG publishes and resets.
 */
import { diag } from '../content/compendium.ts';
import type { Patient, PendingOutcome, Role, SessionState, TableState } from '../types.ts';
import { ROLE_ORDER } from '../types.ts';
import { supportsNeeded, ventsFree } from './act.ts';
import {
  BED_OF,
  clockLength,
  emit,
  listOf,
  nextStep,
  seatedRoles,
  type Ctx,
} from './common.ts';
import { addTicks, deteriorate, die, normaliseRoute, reveal } from './outcomes.ts';

// ───────────────────────────── helpers ─────────────────────────────

function observeInProgress(p: Patient): boolean {
  const st = nextStep(p);
  if (!st || st.kind !== 'OBSERVE' || p.needsStabilise) return false;
  if (p.loc.kind !== 'bed' || !p.seat) return false;
  return st.bed === 'any' || BED_OF[p.seat] === st.bed;
}

function inICUBed(p: Patient): boolean {
  return p.seat === 'ICU' && p.loc.kind === 'bed';
}

function ventNeeded(ctx: Ctx, t: TableState, p: Patient): boolean {
  return !!t.board.vents && inICUBed(p) && (!!diag(p.believedKey).vent || !!p.flags.compInICU);
}

/** Park unanswered interrupts at commitment. */
function parkInterrupts(ctx: Ctx, t: TableState): void {
  for (const i of t.interrupts) {
    if (i.state !== 'NEW') continue;
    i.state = 'PARKED';
    i.age = 0;
    const p = i.event === 'E44' && i.patient ? ctx.s.patients[i.patient] : undefined;
    if (!p || p.status !== 'ACTIVE' || t.timeOut.turn === ctx.s.clock.turn) continue;
    addTicks(p, 1);
    emit(ctx, { type: 'RYAN_UNANSWERED', table: t.no, seat: p.seat ?? undefined, patient: p.id, severity: 2, tag: 'Listen to the family; escalation', text: `Nobody reviewed ${p.name} after the family's call: waiting time +1.`, audience: ['faculty', 'table'] });
  }
}

/** Record staffing shortfall without a second care penalty. */
function staffForSeat(ctx: Ctx, t: TableState, role: Role): void {
  const list = listOf(ctx.s, t, role);
  const need = (p: Patient) => (p.seat === 'ICU' || p.colour === 'RED' ? 2 : 1);
  let staff = t.seats[role]!.staff;
  const order = [...list].sort((a, b) => {
    const pinned = Number(!!b.flags.pinnedStaff) - Number(!!a.flags.pinnedStaff);
    if (pinned) return pinned;
    const critical = need(b) - need(a);
    if (critical) return critical;
    return clockLength(a.colour) - a.ticks - (clockLength(b.colour) - b.ticks);
  });
  for (const p of order) {
    p.flags.unstaffed = staff < need(p);
    if (p.flags.unstaffed) t.dashboard.unstaffedPatientTurns++;
    else staff -= need(p);
  }
}

/** Apply the ordinary unmet-care clock once. */
function tickPatient(ctx: Ctx, t: TableState, p: Patient): void {
  const turn = ctx.s.clock.turn;
  if (inICUBed(p)) t.dashboard.icuBedTurns++;
  if (t.timeOut.turn === turn) return;
  const supported = inICUBed(p) && (p.turnScratch.supports ?? 0) >= supportsNeeded(ctx.s);
  const attended = p.attendedTurn === turn || observeInProgress(p) || supported;
  const arrivedNow = p.arrivedTurn === turn && p.flags.special !== 'regional';
  if (!attended && !arrivedNow) addTicks(p, 1);
  if (inICUBed(p)) supportPatient(ctx, t, p);
}

/** Retain essential ICU support and ventilator consequences. */
function supportPatient(ctx: Ctx, t: TableState, p: Patient): void {
  let ok = (p.turnScratch.supports ?? 0) >= supportsNeeded(ctx.s);
  if (ventNeeded(ctx, t, p) && !p.flags.ventHeld) {
    if (ventsFree(ctx.s, t) > 0) p.flags.ventHeld = true;
    else {
      ok = false;
      t.dashboard.ventShortTurns++;
      emit(ctx, { type: 'VENT_SHORT', table: t.no, seat: 'ICU', patient: p.id, severity: 2, tag: 'Resources', text: `${p.name} needed a ventilator and there was none free.` });
    }
  }
  if (ok) {
    p.icuStreak = 0;
    return;
  }
  p.icuStreak++;
  p.turnScratch.supports = -1;
  t.dashboard.harmed++;
  emit(ctx, { type: 'ICU_UNSUPPORTED', table: t.no, seat: 'ICU', patient: p.id, severity: 2, tag: 'Prioritisation', text: `${p.name} was not supported in ICU this turn: harmed${p.icuStreak >= 2 ? ', and it is the second turn running' : ''}.`, audience: ['faculty', 'seat:ICU'] });
  if (p.icuStreak >= 2) die(ctx, t, p, 'two turns unsupported in ICU');
}

/** Reveal complications and early-departure outcomes already owed. */
function readOutcomes(ctx: Ctx, t: TableState): void {
  const s = ctx.s;
  for (const p of ROLE_ORDER.flatMap((r) => listOf(s, t, r))) {
    const code = p.turnScratch.warnRoute;
    if (!code) continue;
    const back = s.backs[p.id]!;
    const fails = back.complication.includes(code);
    const text = `COMPLICATION: ${back.complication.length ? back.complication.join(' and ') : 'none'}`;
    reveal(ctx, p, 'COMPLICATION', text);
    t.pending.push({ kind: 'COMPLICATION', patient: p.id, seat: p.seat!, route: code, fails, blood: !!p.turnScratch.warnBlood });
    emit(ctx, { type: 'COMPLICATION_LINE', table: t.no, seat: p.seat, patient: p.id, severity: fails ? 2 : 0, tag: 'Bad luck vs bad judgement', text: `${p.name}: ${text}${fails ? ` — the ${code} route complicates.` : '.'} Read it aloud.`, audience: ['faculty', `seat:${p.seat!}`] });
  }
  for (const p of Object.values(s.patients)) {
    if (p.table !== t.no || p.status !== 'DISCHARGED' || p.turnScratch.earlySkipped === undefined) continue;
    const back = s.backs[p.id]!;
    const skipped = p.turnScratch.earlySkipped;
    const fails = (back.early === 'BOUNCE_1' && skipped >= 1) || (back.early === 'BOUNCE_2' && skipped >= 2);
    const text = `EARLY: ${back.early === 'TOLERATES' ? 'tolerates' : back.early === 'BOUNCE_1' ? 'bounces if 1+ Observe skipped' : 'bounces if 2+ Observes skipped'}`;
    reveal(ctx, p, 'EARLY', text);
    const by = p.turnScratch.earlyBy && p.turnScratch.earlyBy !== 'LOCUM' ? p.turnScratch.earlyBy : (p.flags.lastSeat ?? 'EM');
    t.pending.push({ kind: 'EARLY', patient: p.id, seat: by, skipped, fails });
    emit(ctx, { type: 'EARLY_LINE', table: t.no, seat: by, patient: p.id, severity: fails ? 2 : 1, tag: 'Premature closure; flow', text: `${p.name}: ${text}${fails ? ' — will bounce back.' : ' — passed.'} Read it aloud.`, audience: ['faculty', `seat:${by}`] });
  }
}

/** Advance completed passive care outside a Time-Out. */
function advanceCare(ctx: Ctx, t: TableState, p: Patient): void {
  const turn = ctx.s.clock.turn;
  if (t.timeOut.turn === turn) return;
  if (p.ticks >= clockLength(p.colour)) {
    if (p.colour === 'RED') die(ctx, t, p, 'the clock ran out');
    else deteriorate(ctx, t, p, 'the clock ran out');
  }
  if (p.status !== 'ACTIVE' || p.turnScratch.deteriorated || !p.route) return;
  const st = nextStep(p);
  if (st?.kind === 'OBSERVE' && observeInProgress(p)) p.route.next++;
  else if (st?.kind === 'ICU' && inICUBed(p) && (p.turnScratch.supports ?? 0) >= supportsNeeded(ctx.s) && !p.needsStabilise) p.route.next++;
  else return;
  normaliseRoute(p);
  if (nextStep(p)?.kind === 'STEP_DOWN' && t.tableStats.stepDownWaiting[p.id] === undefined) t.tableStats.stepDownWaiting[p.id] = turn;
}

/** Restore expired staff loans without consuming future commitments. */
export function expireLoans(ctx: Ctx, t: TableState, turn: number): void {
  const expired = t.board.staffLoans.filter(loan => loan.fromTurn <= turn && loan.untilTurn <= turn);
  for (const loan of expired) {
    const returned = Math.min(loan.n, t.seats[loan.to]!.staff);
    t.seats[loan.to]!.staff -= returned;
    t.seats[loan.from]!.staff += returned;
    let missing = loan.n - returned;
    t.board.staffAdjust = t.board.staffAdjust.flatMap(adj => {
      if (adj.role !== loan.to || missing === 0) return [adj];
      const n = Math.min(missing, adj.n);
      missing -= n;
      return [...(adj.n > n ? [{ ...adj, n: adj.n - n }] : []), { ...adj, role: loan.from, n }];
    });
    if (returned < loan.n) emit(ctx, { type: 'STAFF_RETURN_SHORT', table: t.no, seat: loan.from, severity: 1, text: `${loan.n - returned} borrowed Staff remain absent; any scheduled return will go to ${loan.from}.`, audience: ['faculty', `seat:${loan.from}`, `seat:${loan.to}`] });
  }
  t.board.staffLoans = t.board.staffLoans.filter(loan => loan.fromTurn > turn || loan.untilTurn > turn);
}

/** Reset turn resources when Control releases the next turn. */
function releaseTableResources(ctx: Ctx, t: TableState): void {
  const turn = ctx.s.clock.turn;
  for (const i of t.interrupts) {
    if (i.state !== 'PARKED') continue;
    i.age++;
    if (i.age < 2) continue;
    i.state = 'ESCALATED';
    emit(ctx, { type: 'INTERRUPT_ESCALATED', table: t.no, seat: i.seat, severity: 1, tag: 'Interruptions', text: `${i.title} was left too long: it will use up one of your actions next turn.`, audience: ['faculty', `seat:${i.seat}`] });
  }
  t.published = { saved: t.dashboard.saved, harmed: t.dashboard.harmed, died: t.dashboard.died };
  t.board.scannerUsed = 0;
  t.board.scannerUsedBy = [null, null];
  t.board.theatre = { booking: null, anaesthetic: null, used: false, ventTaken: false };
  t.board.secondTheatre = null;
  t.board.allocations = { scanner: [null, null], theatre: null };
  t.openCards = [];
  t.effects.e7FirstDischargeUsed = [];
  expireLoans(ctx, t, turn);
  for (const adj of t.board.staffAdjust.filter(a => a.until <= turn)) {
    const st = t.seats[adj.role];
    if (st) st.staff += adj.n;
  }
  t.board.staffAdjust = t.board.staffAdjust.filter(a => a.until > turn);
  t.locums = t.locums.filter(l => l.untilTurn === null || l.untilTurn > turn);
}

// ───────────────────────────── TICK_OPEN ─────────────────────────────

export function tickOpen(ctx: Ctx): void {
  for (const t of ctx.s.tables) {
    parkInterrupts(ctx, t);
    const active = ROLE_ORDER.flatMap((r) => listOf(ctx.s, t, r));
    for (const p of active) p.flags.unstaffed = false;
    if (ctx.s.config.layers.staff) seatedRoles(t).forEach(role => staffForSeat(ctx, t, role));
    for (const p of active) tickPatient(ctx, t, p);
    readOutcomes(ctx, t);
  }
}

/** TICK_OPEN may end early when nothing is waiting on a person. */
export function tickOpenComplete(s: Ctx['s']): boolean {
  return s.tables.every((t) => t.prompts.every((q) => q.phase !== 'TICK_OPEN'));
}

// ───────────────────────────── TICK_CLOSE ─────────────────────────────

function resolvePending(ctx: Ctx, t: TableState, o: PendingOutcome): void {
  const s = ctx.s;
  const p = s.patients[o.patient]!;
  switch (o.kind) {
    case 'COMPLICATION': {
      if (!o.fails || p.status !== 'ACTIVE') break;
      if (p.seat === 'ICU') p.flags.compInICU = true;
      if (o.blood && t.board.blood) {
        if (t.board.blood.units >= 2) {
          t.board.blood.units -= 2;
          t.board.blood.used += 2;
          t.dashboard.bloodUsed += 2;
        } else {
          t.dashboard.harmed++;
          emit(ctx, { type: 'NO_BLOOD', table: t.no, seat: p.seat, patient: p.id, severity: 2, tag: 'Resources', text: `${p.name} bled and the blood bank was empty: further harm.` });
        }
      }
      if (p.colour === 'RED') die(ctx, t, p, `a complication on the ${o.route} route`);
      else deteriorate(ctx, t, p, `a complication on the ${o.route} route`);
      break;
    }
    case 'EARLY': {
      const seat = t.seats[o.seat];
      if (!o.fails) {
        t.dashboard.saved++;
        t.dashboard.early++;
        if (seat) seat.stats.earlyPassed++;
        emit(ctx, { type: 'EARLY_PASSED', table: t.no, seat: o.seat, patient: p.id, text: `${p.name} stayed home.` });
      } else {
        if (seat) seat.stats.earlyBounced++;
        p.status = 'BOUNCE_PENDING';
        t.bounceSlot.push({ id: p.id, returnTurn: s.clock.turn + 2 });
        emit(ctx, { type: 'BOUNCE_PENDING', table: t.no, seat: o.seat, patient: p.id, severity: 2, tag: 'Premature closure; flow', text: `${p.name} will come back in two turns, one colour worse.` });
      }
      break;
    }
  }
}

export function tickClose(ctx: Ctx): void {
  const s = ctx.s;
  for (const t of s.tables) {
    for (const o of t.pending) resolvePending(ctx, t, o);
    t.pending = [];
    t.prompts = t.prompts.filter((q) => q.phase !== 'TICK_OPEN');
    for (const p of ROLE_ORDER.flatMap((r) => listOf(s, t, r))) advanceCare(ctx, t, p);
    publishBedCounts(s, t);
  }
}

/** Post each seat's bed and corridor counts on the shared board (at the Tick, and once at the start). */
export function publishBedCounts(s: SessionState, t: TableState): void {
  for (const role of ROLE_ORDER) {
    const st = t.seats[role];
    if (!st) continue;
    const list = listOf(s, t, role);
    t.board.bedCounts[role] = { used: list.filter((p) => p.loc.kind === 'bed').length, cap: st.beds.length, corridor: list.filter((p) => p.loc.kind === 'corridor').length };
  }
}

// ───────────────────────────── LOG ─────────────────────────────

export function logPhase(ctx: Ctx): void {
  for (const t of ctx.s.tables) releaseTableResources(ctx, t);
  for (const p of Object.values(ctx.s.patients)) {
    if (p.status === 'DECK') continue;
    p.turnScratch = { stepsDone: 0 };
  }
}

export { ventNeeded };
